import React, { useState, useEffect } from "react";
import { fetchVideoList } from "../api"; // Import the API function
import { ImFilePlay } from "react-icons/im";
import { RiFileCheckLine } from "react-icons/ri";
import { RiFileCloseLine } from "react-icons/ri";
import { IoSearchOutline } from "react-icons/io5"; // Add search icon
import "./VideoDetectionList.css"; // Import the CSS file for styling

const VideoDetectionList = ({ selectedVideoId }) => {
  const [videos, setVideos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // 'all', 'labeled', 'unlabeled'

  const loadVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      const videoData = await fetchVideoList();
      setVideos(videoData);
    } catch (error) {
      console.error("Error loading videos:", error);
      setError("Failed to load videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVideos();
  }, []);

  const handleVideoClick = (video) => {
    const url = `/video-playback?videoId=${video.video_id}`;
    const state = JSON.stringify({ video });
    window.open(`${url}&state=${encodeURIComponent(state)}`, "_blank");
  };

  const getAllVideos = () => {
    if (!videos) return [];
    const labeled = videos.labeled_videos || [];
    const nonLabeled = videos.non_labeled_videos || [];
    return [...labeled, ...nonLabeled];
  };

  const getFilteredVideos = () => {
    let filteredVideos = [];

    switch (activeTab) {
      case "labeled":
        filteredVideos = videos?.labeled_videos || [];
        break;
      case "unlabeled":
        filteredVideos = videos?.non_labeled_videos || [];
        break;
      default:
        filteredVideos = getAllVideos();
    }

    if (searchInput) {
      filteredVideos = filteredVideos.filter((video) =>
        video.video_name.toLowerCase().includes(searchInput.toLowerCase())
      );
    }

    return filteredVideos;
  };

  // New function to get filtered counts for stats
  const getFilteredCounts = () => {
    const allVideos = getAllVideos();
    const labeledVideos = videos?.labeled_videos || [];
    const unlabeledVideos = videos?.non_labeled_videos || [];

    // Apply search filter to each category
    const filteredAll = searchInput
      ? allVideos.filter((video) =>
        video.video_name.toLowerCase().includes(searchInput.toLowerCase())
      )
      : allVideos;

    const filteredLabeled = searchInput
      ? labeledVideos.filter((video) =>
        video.video_name.toLowerCase().includes(searchInput.toLowerCase())
      )
      : labeledVideos;

    const filteredUnlabeled = searchInput
      ? unlabeledVideos.filter((video) =>
        video.video_name.toLowerCase().includes(searchInput.toLowerCase())
      )
      : unlabeledVideos;

    return {
      total: filteredAll.length,
      labeled: filteredLabeled.length,
      unlabeled: filteredUnlabeled.length,
    };
  };

  // Add function to get the correct total for the footer
  const getTotalForCurrentView = () => {
    const filteredCounts = getFilteredCounts();

    switch (activeTab) {
      case "labeled":
        return filteredCounts.labeled;
      case "unlabeled":
        return filteredCounts.unlabeled;
      default:
        return filteredCounts.total;
    }
  };

  const getVideoStatus = (video) => {
    const labeled = videos?.labeled_videos || [];
    return labeled.some((v) => v.video_id === video.video_id)
      ? "Labeled"
      : "Unlabeled";
  };

  const getStatusButtonClass = (status) => {
    return status === "Labeled"
      ? "status-button labeled"
      : "status-button unlabeled";
  };

  if (loading) {
    return (
      <div className="video-detection-container">
        <div className="loading-message">Loading videos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="video-detection-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={loadVideos}>Retry</button>
        </div>
      </div>
    );
  }

  const filteredVideos = getFilteredVideos();
  const filteredCounts = getFilteredCounts();
  const totalForCurrentView = getTotalForCurrentView();

  return (
    <div className="video-detection-container">
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-icon-wrapper first">
            <ImFilePlay className="stat-icon" />
          </div>
          <div className="stat-content">
            <span className="stat-label">
              Total Videos {searchInput && "(filtered)"}
            </span>
            <span className="stat-value">{filteredCounts.total}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper second">
            <RiFileCheckLine className="stat-icon" />
          </div>
          <div className="stat-content">
            <span className="stat-label">
              Labeled Videos {searchInput && "(filtered)"}
            </span>
            <span className="stat-value">{filteredCounts.labeled}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon-wrapper third">
            <RiFileCloseLine className="stat-icon" />
          </div>
          <div className="stat-content">
            <span className="stat-label">
              Unlabeled Videos {searchInput && "(filtered)"}
            </span>
            <span className="stat-value">{filteredCounts.unlabeled}</span>
          </div>
        </div>
      </div>

      <div className="video-table-container">
        <div className="table-header">
          <div className="tab-section">
            <button
              className={`tab-button ${activeTab === "all" ? "active" : ""}`}
              onClick={() => setActiveTab("all")}
            >
              All Videos
            </button>
            <button
              className={`tab-button ${activeTab === "labeled" ? "active" : ""
                }`}
              onClick={() => setActiveTab("labeled")}
            >
              Labeled Videos
            </button>
            <button
              className={`tab-button ${activeTab === "unlabeled" ? "active" : ""
                }`}
              onClick={() => setActiveTab("unlabeled")}
            >
              Unlabeled Videos
            </button>
          </div>

          <div className="table-controls">
            <div className="search-container">
              <IoSearchOutline className="search-icon" />
              <input
                placeholder="Search videos..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="search-input"
              />
            </div>

            {/* Sorting functionality commented out for now */}
            {/* <button className="sort-button">
              <span>Sort by: Newest</span>
              <img
                src="https://storage.googleapis.com/tagjs-prod.appspot.com/v1/pP5ulGK3xn/3bqmhg5f_expires_30_days.png"
                className="sort-icon"
              />
            </button> */}
          </div>
        </div>

        <div className="video-table">
          <div className="table-header-row">
            <span className="header-cell">Video Name</span>
            {/* <span className="header-cell">Video ID</span> */}
            <span className="header-cell">Labeled By</span>
            <span className="header-cell">Diagnosis</span>
            <span className="header-cell">Diagnosis Probability</span>
            <span className="header-cell">Status</span>
            <span className="header-cell">Actions</span>
          </div>

          {filteredVideos.map((video, index) => {
            const diagnosisLabels = {
              0: "None",
              1: "Low",
              2: "Middle",
              3: "High",
              4: "Positive",
              5: "Negative"
            };

            return (
              <div key={video.video_id} className="table-row">
                <span className="table-cell video-name">{video.video_name}</span>
                {/* <span className="table-cell">{video.video_id}</span> */}
                <span className="table-cell">
                  {video.users_labeled && video.users_labeled.length > 0
                    ? video.users_labeled.join(", ")
                    : "None"}
                </span>
                <span className="table-cell">
                  {video.diagnosis !== null && video.diagnosis !== undefined
                    ? diagnosisLabels[video.diagnosis] || "Unknown"
                    : "NA"}
                </span>
                <span className="table-cell">
                  {video.diagnosis_probability !== null && video.diagnosis_probability !== undefined
                    ? `${(video.diagnosis_probability * 100).toFixed(2)}%`
                    : "NA"}
                </span>
                <span className="table-cell">
                  <button className={getStatusButtonClass(getVideoStatus(video))}>
                    {getVideoStatus(video)}
                  </button>
                </span>
                <span className="table-cell">
                  <button
                    className="action-button"
                    onClick={() => handleVideoClick(video)}
                  >
                    View
                  </button>
                </span>
              </div>
            );
          })}
        </div>

        <div className="table-footer">
          <span className="table-info">
            Showing {filteredVideos.length} of {totalForCurrentView} videos
          </span>

          {/* Pagination commented out for now */}
          {/* <div className="pagination">
            <button className="page-button">{"<"}</button>
            <button className="page-button active">1</button>
            <button className="page-button">2</button>
            <button className="page-button">3</button>
            <span className="page-dots">...</span>
            <button className="page-button">{">"}</button>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default VideoDetectionList;
